import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";

const SCHEMA_HOWTO = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to write LinkedIn posts with AI using Lakshmi",
    "description": "Step-by-step guide to writing high-quality LinkedIn posts using Lakshmi — an AI LinkedIn post writer built for Indian professionals.",
    "step": [
        { "@type": "HowToStep", "position": 1, "name": "Sign up free", "text": "Create your free Lakshmi account at hilakshmi.com. No credit card required." },
        { "@type": "HowToStep", "position": 2, "name": "Set your brand voice", "text": "Paste one LinkedIn post you're proud of. Lakshmi analyses your tone and style." },
        { "@type": "HowToStep", "position": 3, "name": "Drop your idea", "text": "Type a rough idea — even half a sentence. Lakshmi's Content Strategist turns it into a hook." },
        { "@type": "HowToStep", "position": 4, "name": "Get your post", "text": "In under 60 seconds, receive a complete LinkedIn post: hook, body, CTA, hashtags — in your voice." },
        { "@type": "HowToStep", "position": 5, "name": "Review and post", "text": "Check the quality score, tweak if needed, copy to LinkedIn and post." }
    ],
    "totalTime": "PT1M",
    "tool": [{ "@type": "HowToTool", "name": "Lakshmi AI", "url": "https://hilakshmi.com" }]
};

const BENEFITS = [
    "Hook that stops the scroll — every time",
    "Body with a real story, not bullet points",
    "CTA that drives comments and connections",
    "Hashtags that reach the right audience",
    "Your voice — not ChatGPT's",
    "Quality scored before you post",
];

export default function UseCaseLinkedIn() {
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.text = JSON.stringify(SCHEMA_HOWTO);
        document.head.appendChild(s);
        document.title = "Write LinkedIn Posts with AI — Lakshmi | AI LinkedIn Post Writer India";
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", "Learn how to write LinkedIn posts with AI using Lakshmi — India's best AI LinkedIn post writer. 60-second drafts in your voice, with hooks that stop the scroll.");
        return () => s.remove();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">

                {/* Hero */}
                <div className="flex flex-col lg:flex-row items-center gap-12 mb-16">
                    <div className="flex-1 text-center lg:text-left">
                        <div className="lk-label mb-3">Use case — LinkedIn</div>
                        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-6">
                            Write LinkedIn posts with AI <span className="lk-gradient-text">in under 60 seconds</span>
                        </h1>
                        <p className="text-[var(--lk-muted)] text-base sm:text-lg max-w-xl mb-8">
                            Lakshmi is the AI LinkedIn post writer built for Indian professionals — founders, engineers, sales leaders, and consultants who want to grow their personal brand without spending hours writing.
                        </p>
                        <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                            Write my first LinkedIn post free <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    {/* Mock input → output UI */}
                    <div className="flex-shrink-0 w-full max-w-sm space-y-3">
                        <div className="lk-border lk-surface rounded-xl p-4">
                            <div className="text-xs text-[var(--lk-muted)] mb-2 font-medium uppercase tracking-wider">Your idea</div>
                            <div className="text-sm italic text-[var(--lk-muted)]">"I scaled our API from 10K to 10M requests/day. Here's what broke first…"</div>
                        </div>
                        <div className="flex justify-center">
                            <div className="flex flex-col items-center gap-1">
                                <div className="w-0.5 h-4 bg-[var(--lk-primary)]/50 rounded" />
                                <div className="px-3 py-1 rounded-full text-xs font-semibold"
                                    style={{ background: "rgba(124,58,237,0.15)", color: "#9B5CF6", border: "1px solid rgba(124,58,237,0.3)" }}>
                                    Lakshmi 4-agent pipeline · 47s
                                </div>
                                <div className="w-0.5 h-4 bg-[var(--lk-primary)]/50 rounded" />
                            </div>
                        </div>
                        <div className="lk-border rounded-xl p-4" style={{ borderColor: "rgba(124,58,237,0.4)", background: "rgba(124,58,237,0.04)" }}>
                            <div className="text-xs text-[var(--lk-primary)] mb-2 font-medium uppercase tracking-wider">LinkedIn post — ready to publish</div>
                            <p className="text-sm leading-relaxed">
                                Our backend crashed at 2 AM on a Tuesday.<br /><br />
                                I had 47 unread Slack messages, a pager going off, and a CEO asking "what's the ETA?"<br /><br />
                                What I learned rebuilding that API from scratch taught me more than 3 years of normal scaling…
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                                <div className="px-2 py-0.5 rounded-full text-xs font-semibold"
                                    style={{ background: "rgba(52,211,153,0.15)", color: "#34D399" }}>
                                    Score: 91/100
                                </div>
                                <span className="text-xs text-[var(--lk-muted)]">Hook · Story · CTA · Hashtags included</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Benefits */}
                <div className="lk-border lk-surface rounded-2xl p-8 mb-12">
                    <h2 className="font-semibold text-xl mb-6">What every Lakshmi LinkedIn post includes</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {BENEFITS.map(b => (
                            <div key={b} className="flex items-start gap-3 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                <span>{b}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* How it works */}
                <div className="mb-12">
                    <h2 className="font-heading text-2xl sm:text-4xl mb-8">How to write LinkedIn posts with AI — step by step</h2>
                    <div className="space-y-4">
                        {SCHEMA_HOWTO.step.map(step => (
                            <motion.div key={step.position}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: step.position * 0.08 }}
                                viewport={{ once: true }}
                                className="flex gap-5 lk-border lk-surface rounded-xl p-5"
                            >
                                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm"
                                    style={{ background: "rgba(124,58,237,0.15)", color: "#9B5CF6" }}>
                                    {step.position}
                                </div>
                                <div>
                                    <div className="font-semibold mb-1">{step.name}</div>
                                    <div className="text-sm text-[var(--lk-muted)]">{step.text}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Why better than ChatGPT */}
                <div className="lk-border rounded-2xl p-8 mb-12"
                    style={{ background: "rgba(124,58,237,0.04)", borderColor: "rgba(124,58,237,0.3)" }}>
                    <h2 className="font-semibold text-xl mb-4">Why Lakshmi beats ChatGPT for LinkedIn posts</h2>
                    <div className="grid sm:grid-cols-2 gap-6 text-sm">
                        <div>
                            <div className="text-[var(--lk-muted)] font-medium mb-3">ChatGPT / generic AI</div>
                            <ul className="space-y-2 text-[var(--lk-muted)]">
                                <li>• Writes generic "thought leadership" prose</li>
                                <li>• No LinkedIn-specific hook structure</li>
                                <li>• Doesn't know your voice or industry</li>
                                <li>• No quality gate before posting</li>
                                <li>• Single model, single pass</li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-[var(--lk-primary)] font-medium mb-3">Lakshmi</div>
                            <ul className="space-y-2">
                                <li>• Hooks calibrated for LinkedIn scroll-stopping</li>
                                <li>• 4-agent pipeline: strategy → voice → write → QA</li>
                                <li>• Learns your specific writing style</li>
                                <li>• Quality scored 0–100, retried if low</li>
                                <li>• Built specifically for Indian professionals</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="text-center">
                    <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                        Start writing LinkedIn posts for free <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
